import { useCallback, useEffect, useState } from "react";

import { Collapse, Input, Select, Switch } from "antd";
import { EVA_RESEARCH_NAME } from "constants/app.constants";
import _debounce from "lodash/debounce";

import usePlayTo3DFields from "hooks/usePlayTo3DFields";
import useUserModules from "hooks/useUserModules";

import { ThreeDViewerOptionT } from "models";

import { LabeledSwitch } from "components/base";

import {
  ThreeDVOptionItem,
  ThreeDVOptionLabel,
  ThreeDVOptionsWrapper
} from "./ThreeDVOptionsLayout";

const { Option } = Select;
const { Panel } = Collapse;

function ThreeDVGeoValueOptions(props: ThreeDViewerOptionT): JSX.Element {
  const [colorMapMin, setColorMapMin] = useState<string>(
    props.colorMapMin?.toString() ?? ""
  );
  const [colorMapMax, setColorMapMax] = useState<string>(
    props.colorMapMax?.toString() ?? ""
  );

  const { has3dGeoModel } = useUserModules();

  // Initialise from the 3D viewer's own props (which carry the saved/default value).
  const [ipdbField, setIpdbField] = useState(props.ipdbField);
  const [ipdbSource, setIpdbSource] = useState(props.ipdbSource);
  const { data: fields, refetch } = usePlayTo3DFields(ipdbSource);
  useEffect(() => {
    if (ipdbSource) refetch();
  }, [ipdbSource]);

  const debouncedOnChange = useCallback(
    _debounce((val, refresh) => {
      props.onOptionChange && props.onOptionChange(val, refresh);
    }, 500),
    [props.onOptionChange]
  );

  // Keep local state in sync with ThreeDViewer's own options (the source of truth).
  // This handles the case where ThreeDViewer updates options externally, e.g. the
  // geoSettings effect applying a user default after mount.
  // NOTE: we intentionally do NOT sync from xda.settings here — the 3D widget's
  // XdaSettingsStoreProvider has no initialState so xda.settings always resolves to user defaults,
  // which would overwrite DB-saved or user-selected values on every render.
  useEffect(() => {
    setIpdbSource(props.ipdbSource);
  }, [props.ipdbSource]);

  useEffect(() => {
    setIpdbField(props.ipdbField);
  }, [props.ipdbField]);

  // restore to saved field or first item
  useEffect(() => {
    if (!fields || !fields?.some((f) => f.name === ipdbField)) {
      return;
    } else {
      const initField = fields[0];
      setIpdbField(initField.name);
      props.onOptionChange?.(
        { ipdbField: initField.name, ipdbFieldUnit: initField.unit ?? "" },
        false
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  return (
    <ThreeDVOptionsWrapper>
      <LabeledSwitch
        label={{ value: "Show Well Volume" }}
        switch={{
          isChecked: props.showDrainage,
          onChange: (value) => {
            props.onOptionChange && props.onOptionChange({ showDrainage: value }, true);
          }
        }}
      />
      {(has3dGeoModel || !!props.ipdbSources?.length) && (
        <Collapse accordion>
          <Panel
            header="Well Geo Values"
            key="IPDB"
            collapsible={props.showDrainage ? "header" : "disabled"}
            extra={
              <Switch
                className="action"
                disabled={!props.showDrainage}
                checked={props.showDrainage && props.showIPDBDrainage}
                onChange={(e) => {
                  props.onOptionChange &&
                    props.onOptionChange({ showIPDBDrainage: e }, true);
                }}
              />
            }>
            <ThreeDVOptionItem>
              <label>Model Source</label>
              <Select
                size="small"
                value={ipdbSource}
                options={props.ipdbSources?.map((source) => ({
                  label: source == "McDaniel" ? EVA_RESEARCH_NAME : source,
                  value: source
                }))}
                onChange={(source) => {
                  setIpdbSource(source);
                  props.onOptionChange({ ipdbSource: source }, true);
                }}
              />
            </ThreeDVOptionItem>

            <ThreeDVOptionItem>
              <ThreeDVOptionLabel>Field</ThreeDVOptionLabel>
              <Select
                size="small"
                value={ipdbField}
                options={fields?.map((field) => ({
                  label: field.displayName,
                  value: field.name,
                  title: field.tooltip ?? field.displayName
                }))}
                onChange={(value) => {
                  setIpdbField(value);
                  const field = fields?.find((f) => f.name === value);
                  props.onOptionChange &&
                    props.onOptionChange(
                      {
                        ipdbField: value,
                        ipdbFieldUnit: field?.unit ?? ""
                      },
                      false
                    );
                }}
              />
            </ThreeDVOptionItem>

            <ThreeDVOptionItem>
              <label>Colour</label>
              <Select
                value={props.colorMap}
                onChange={(e) => {
                  props.onOptionChange && props.onOptionChange({ colorMap: e }, false);
                }}>
                <Option value="rainbow" key="rainbow">
                  Rainbow
                </Option>
                <Option value="cooltowarm" key="cooltowarm">
                  Cool to Warm
                </Option>
                <Option value="blackbody" key="blackbody">
                  Blackbody
                </Option>
              </Select>
            </ThreeDVOptionItem>

            <ThreeDVOptionItem>
              <label>Min</label>
              <Input
                value={colorMapMin}
                onChange={(e) => {
                  setColorMapMin(e.target.value);
                  debouncedOnChange(
                    {
                      colorMapMin: e.target.valueAsNumber
                    },
                    false
                  );
                }}
                type="number"
              />
            </ThreeDVOptionItem>

            <ThreeDVOptionItem>
              <label>Max</label>
              <Input
                value={colorMapMax}
                onChange={(e) => {
                  setColorMapMax(e.target.value);
                  debouncedOnChange(
                    {
                      colorMapMax: e.target.valueAsNumber
                    },
                    false
                  );
                }}
                type="number"
              />
            </ThreeDVOptionItem>
          </Panel>
        </Collapse>
      )}
    </ThreeDVOptionsWrapper>
  );
}

export default ThreeDVGeoValueOptions;
